<template>
	<div class="qr-scanner-container" v-if="isScanning">
		<div v-if="isScanning" class="scanner-overlay">
			<div class="scanner-frame">
				<div class="scanner-corners">
					<div class="corner top-left"></div>
					<div class="corner top-right"></div>
					<div class="corner bottom-left"></div>
					<div class="corner bottom-right"></div>
				</div>
			</div>

			<video ref="videoElement" autoplay playsinline></video>
			<div class="scanner-controls">
				<button v-if="availableCameras.length > 1" @click="cycleToNextCamera" class="switch-camera-button">
					<i class="fa-solid fa-camera"></i>
				</button>
				<button @click="stopScanningClick" class="close-button">×</button>
			</div>
		</div>

		<div v-if="error" class="error-message">
			{{ error }}
		</div>
	</div>
</template>

<script setup>
import { ref, onUnmounted, onMounted, watch } from 'vue'
import { useAppStore } from '@/stores/app'
import jsQR from 'jsqr'

const appStore = useAppStore()

const props = defineProps({
	onSuccess: {
		type: Function,
		required: true,
	},
	autoStart: {
		type: Boolean,
		default: false,
	},
	active: {
		type: Boolean,
		default: false,
	},
	defaultCamera: {
		type: String,
		default: 'environment', // 'environment' for back camera, 'user' for front camera
		validator: (value) => ['environment', 'user'].includes(value),
	},
})

const emit = defineEmits(['scanned', 'error', 'scanningStarted', 'scanningStopped', 'camerasDetected'])

const videoElement = ref(null)
const isScanning = ref(false)
const error = ref('')
const currentCamera = ref(props.defaultCamera)
const availableCameras = ref([])
const selectedDeviceId = ref('')

let stream = null
let scanInterval = null

// Get all available video input devices
const getAvailableCameras = async () => {
	try {
		const devices = await navigator.mediaDevices.enumerateDevices()
		const videoDevices = devices.filter((device) => device.kind === 'videoinput')

		// Map devices to a more useful format
		availableCameras.value = videoDevices.map((device) => ({
			deviceId: device.deviceId,
			label: device.label || `Camera ${availableCameras.value.length + 1}`,
			facing: device.label.toLowerCase().includes('back') ? 'environment' : device.label.toLowerCase().includes('front') ? 'user' : 'unknown',
		}))

		emit('camerasDetected', availableCameras.value)
		return availableCameras.value
	} catch (err) {
		console.error('Error enumerating devices:', err)
		error.value = 'Could not detect cameras'
		emit('error', error.value)
		return []
	}
}

const selectCamera = async (deviceId) => {
	// Stop current stream
	if (stream) {
		stream.getTracks().forEach((track) => track.stop())
		stream = null
	}

	// Find the selected camera to get its facing mode
	const selectedCamera = availableCameras.value.find((camera) => camera.deviceId === deviceId)
	if (selectedCamera && selectedCamera.facing !== 'unknown') {
		currentCamera.value = selectedCamera.facing
	}

	// Start scanning with the selected device
	await startScanning(null, deviceId)

	return true
}

const cycleToNextCamera = () => {
	if (availableCameras.value.length <= 1) return

	// Find current camera index
	const currentIndex = availableCameras.value.findIndex((camera) => camera.deviceId === selectedDeviceId.value)

	// Calculate next camera index (cycle through the list)
	const nextIndex = (currentIndex + 1) % availableCameras.value.length
	const nextCamera = availableCameras.value[nextIndex]

	// Select the next camera
	selectCamera(nextCamera.deviceId)
}

const startScanning = async (cameraFacingMode = null, deviceId = null) => {
	try {
		// If no cameras are loaded yet, get them
		if (availableCameras.value.length === 0) {
			await getAvailableCameras()
		}

		// If we have cameras and no specific device is selected, pick the first one
		if (availableCameras.value.length > 0 && !selectedDeviceId.value && !deviceId) {
			// Try to find a back camera first
			const backCamera = availableCameras.value.find((camera) => camera.facing === 'environment' || camera.label.toLowerCase().includes('back'))

			if (backCamera) {
				deviceId = backCamera.deviceId
			} else {
				// Otherwise just use the first camera
				deviceId = availableCameras.value[0].deviceId
			}
		}

		// If a specific camera is requested by facing mode, use it
		if (cameraFacingMode) {
			currentCamera.value = cameraFacingMode
		}

		// If a specific device ID is provided, use that
		if (deviceId) {
			selectedDeviceId.value = deviceId
		}

		error.value = ''
		isScanning.value = true

		appStore.bottomSheet.setHeightFullscreen(600, 'QrScanner', true)

		// Build the camera constraints
		const constraints = {
			video: {
				width: { ideal: 1280 },
				height: { ideal: 720 },
			},
		}

		// If we have a selected deviceId, use it
		if (selectedDeviceId.value) {
			constraints.video.deviceId = { exact: selectedDeviceId.value }
		} else {
			// Otherwise fall back to facing mode
			constraints.video.facingMode = currentCamera.value
		}

		// Request camera access with the selected camera
		stream = await navigator.mediaDevices.getUserMedia(constraints)

		// Set video source to camera stream
		if (videoElement.value) {
			videoElement.value.srcObject = stream
		}

		// Start scanning for QR codes
		scanInterval = setInterval(scanVideoForQR, 100)
	} catch (err) {
		error.value = 'Camera access denied or not available'
		isScanning.value = false
		emit('error', error.value)
		console.error('Camera error:', err)
	}
}

const stopScanningClick = () => {
	//appStore.bottomSheet.autoResize(300, true, true, 'QRScanner')
	stopScanning()
}

const stopScanning = () => {
	if (scanInterval) {
		clearInterval(scanInterval)
		scanInterval = null
	}

	if (stream) {
		stream.getTracks().forEach((track) => track.stop())
		stream = null
	}

	if (videoElement.value) {
		videoElement.value.srcObject = null
	}

	isScanning.value = false

	if (props.onSuccess) {
		props.onSuccess()
	}

	emit('scanningStopped')
	console.log('Scanning stopped')
}

const scanVideoForQR = () => {
	if (!videoElement.value || !isScanning.value) return

	const video = videoElement.value

	// videoElement.setAttribute('playsinline', true)
	// videoElement.setAttribute('autoplay', true)
	// videoElement.controls = true
	// videoElement.playsinline = true

	// Make sure video is playing
	if (video.readyState !== video.HAVE_ENOUGH_DATA) return

	// Create canvas to capture video frame
	const canvas = document.createElement('canvas')
	const context = canvas.getContext('2d')
	canvas.width = video.videoWidth
	canvas.height = video.videoHeight

	// Draw current video frame to canvas
	context.drawImage(video, 0, 0, canvas.width, canvas.height)

	// Get image data for QR code detection
	const imageData = context.getImageData(0, 0, canvas.width, canvas.height)

	// Scan for QR code
	const qrCode = jsQR(imageData.data, imageData.width, imageData.height)

	// QR code found
	if (qrCode && qrCode.data && qrCode.data.length > 4) {
		handleSuccessfulScan(qrCode.data)
	}
}

const playBeep = () => {
	const audioCtx = new (window.AudioContext || window.webkitAudioContext)()
	const oscillator = audioCtx.createOscillator()
	oscillator.type = 'sine'
	oscillator.frequency.value = 1000 // Frequency in Hz
	oscillator.connect(audioCtx.destination)
	oscillator.start()
	oscillator.stop(audioCtx.currentTime + 0.2) // Beep for 200ms
}

const handleSuccessfulScan = (qrData) => {
	console.log('QR code scanned:', qrData)

	// Play beep sound
	playBeep()

	// Stop scanning
	stopScanning()

	// Call success callback and emit event
	if (props.onSuccess) {
		props.onSuccess(qrData)
	}
	emit('scanned', qrData)
}

const switchCamera = async () => {
	// Toggle between front and back camera
	const newCamera = currentCamera.value === 'environment' ? 'user' : 'environment'

	// Stop current stream
	if (stream) {
		stream.getTracks().forEach((track) => track.stop())
		stream = null
	}

	// Restart scanning with new camera
	await startScanning(newCamera)
}

// Watch for active prop changes to start/stop scanning
watch(
	() => props.active,
	(newValue) => {
		if (newValue && !isScanning.value) {
			startScanning()
		} else if (!newValue && isScanning.value) {
			stopScanning()
		}
	},
)

// Watch for defaultCamera prop changes
watch(
	() => props.defaultCamera,
	(newValue) => {
		if (isScanning.value && currentCamera.value !== newValue) {
			switchCamera()
		} else {
			currentCamera.value = newValue
		}
	},
)

// Load available cameras on component mount
onMounted(async () => {
	await getAvailableCameras()

	if (props.autoStart) {
		startScanning()
	}
})

// Clean up on component unmount
onUnmounted(() => {
	if (isScanning.value) stopScanning()
})

// Expose methods to parent components
defineExpose({
	startScanning,
	stopScanning,
	cycleToNextCamera,
	selectCamera,
	getAvailableCameras,
	isScanning,
	currentCamera,
	availableCameras,
})
</script>

<style scoped>
.qr-scanner-container {
	position: relative;
	width: 100%;
	height: 300px; /* Adjust height based on your design */
	display: flex;
	flex-direction: column;
	align-items: center;
}

.scanner-overlay {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: 1000;
	background-color: rgba(0, 0, 0, 0.8);
	display: flex;
	justify-content: center;
	align-items: center;
}

.scanner-frame {
	position: absolute;
	width: 35%;
	max-width: 500px;
	aspect-ratio: 1;
	border: 2px solid transparent;
	z-index: 1002;
}

.scanner-corners {
	position: relative;
	width: 100%;
	height: 100%;
}

.corner {
	position: absolute;
	width: 30px;
	height: 30px;
	border-color: rgba(255, 255, 255, 0.5);
	border-style: solid;
	border-width: 0;
}

.top-left {
	top: 0;
	left: 0;
	border-top-width: 4px;
	border-left-width: 4px;
}

.top-right {
	top: 0;
	right: 0;
	border-top-width: 4px;
	border-right-width: 4px;
}

.bottom-left {
	bottom: 0;
	left: 0;
	border-bottom-width: 4px;
	border-left-width: 4px;
}

.bottom-right {
	bottom: 0;
	right: 0;
	border-bottom-width: 4px;
	border-right-width: 4px;
}

video {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	object-fit: cover;
	z-index: 1001;
}

.scanner-controls {
	position: absolute;
	top: 1rem;
	right: 1rem;
	display: flex;
	gap: 1rem;
	z-index: 1003;
}

.close-button,
.switch-camera-button {
	width: 3rem;
	height: 3rem;
	border-radius: 50%;
	background-color: rgba(255, 255, 255, 0.7);
	color: black;
	font-size: 2rem;
	border: none;
	cursor: pointer;
	display: flex;
	justify-content: center;
	align-items: center;
}

.switch-camera-button {
	font-size: 18px;
}

.switch-camera-button svg {
	width: 20px;
	height: 20px;
}

.error-message {
	margin-top: 16px;
	color: #ff3333;
	text-align: center;
}
</style>
